import React, { useRef, useState, useMemo } from "react";
import {
  Form,
  Button,
  Card,
  Alert,
  Container,
  ButtonGroup,
  ProgressBar,
  Row,
  Col,
  ListGroup,
  InputGroup,
  Badge,
  Stack,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../firebaseContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelopeCircleCheck,
  faSquarePlus,
  faPaperPlane,
  faUserPlus,
  faUndo,
  faCircleInfo,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

export default function CreateUser() {
  const emailRef = useRef();
  const { SendPasswordLink } = useAuth();
  const [error, setError] = useState("");
  const [alert, setAlert] = useState("");
  const [loading, setLoading] = useState(false);
  const [multiple, setMultiple] = useState(false);
  const [emails, setEmails] = useState([]);
  const [progressBar, setProgressBar] = useState(false);
  const [progress, setProgress] = useState(0);
  const [emailInput, setEmailInput] = useState(""); // NEW
  const navigate = useNavigate();

  function handleAddField(e) {
    e.preventDefault();
    setMultiple(true);
    const currentEmail = emailInput.trim();
    if (currentEmail && !emails.includes(currentEmail)) {
      setEmails((prev) => [...prev, currentEmail]);
      setEmailInput(""); // Clear input after adding
    }
  }

  function handleCancel(e) {
    e.preventDefault();
    setMultiple(false);
    setEmails([]);
    setAlert("");
    setError("");
    setProgressBar(false);
    setProgress(0);
    setEmailInput(""); // Clear input
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setAlert("");
    setLoading(true);

    try {
      if (multiple && emails.length > 0) {
        let sentCount = 0;
        setProgressBar(true);
        for (const email of emails) {
          await SendPasswordLink(email);
          sentCount++;
          setProgress(Math.round((sentCount / emails.length) * 100));
        }
        setAlert(`Sign in links to ${emails.length} emails have been sent!`);
        setEmails([]);
        setMultiple(false);
      } else {
        await SendPasswordLink(emailInput);
        setAlert("Email sent!");
        setEmailInput("");
      }
    } catch (err) {
      setError(err.message || "Failed to send email.");
    }
    setLoading(false);
    setProgressBar(false);
    setProgress(0);
  }

  const pendingCount = useMemo(() => emails.length, [emails]);
  const sendingModeLabel = multiple
    ? `Sending ${pendingCount} invitations`
    : "Single invitation";

  return (
    <Container fluid className="py-5 create-user-page">
      <Row className="justify-content-center">
        <Col xl={8} lg={9}>
          <Card className="border-0 shadow-sm text-white bg-primary mb-4">
            <Card.Body>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                  <Badge bg="light" text="dark" className="text-uppercase mb-2">
                    Invite team members
                  </Badge>
                  <h1 className="display-6 fw-semibold mb-1">
                    Create new access
                  </h1>
                  <p className="mb-0">
                    Send secure sign-in links to one or many colleagues. We’ll
                    handle the welcome email.
                  </p>
                </div>
                <div className="text-lg-end">
                  <p className="text-uppercase small text-white-50 mb-1">
                    Mode
                  </p>
                  <h2 className="fw-bold mb-2">
                    <FontAwesomeIcon
                      icon={faEnvelopeCircleCheck}
                      className="me-2"
                    />
                    {sendingModeLabel}
                  </h2>
                  {multiple && (
                    <Badge bg="light" text="dark">
                      Pending list · {pendingCount}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button
                  variant="light"
                  className="text-primary"
                  onClick={() => navigate(-1)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to admin panel
                </Button>
                <Button
                  variant="success"
                  onClick={() => navigate("/manageUsers")}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                  View user directory
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Row className="g-4 align-items-start">
            <Col lg={7}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Stack gap={3}>
                    <div>
                      <h2 className="h4 mb-1">Invitation details</h2>
                      <p className="text-muted mb-0">
                        Add addresses individually or build a batch list. We’ll
                        send the portal sign-in instructions instantly.
                      </p>
                    </div>

                    <Alert variant="warning" className="mb-0">
                      <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
                      Emails arrive from{" "}
                      <strong>noreply@portal.nobilitycare.com.au</strong>. Ask
                      recipients to check junk folders.
                    </Alert>

                    {error && (
                      <Alert variant="danger" className="mb-0">
                        {error}
                      </Alert>
                    )}
                    {alert && (
                      <Alert variant="success" className="mb-0">
                        {alert}
                      </Alert>
                    )}

                    {multiple && emails.length > 0 && (
                      <Card className="bg-light border-0">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold">
                              Bulk invitation list
                            </span>
                            <Badge bg="secondary">{pendingCount} emails</Badge>
                          </div>
                          <ListGroup variant="flush" className="mb-0">
                            {emails.map((email, idx) => (
                              <ListGroup.Item
                                key={email + idx}
                                className="d-flex align-items-center justify-content-between px-0 py-2"
                              >
                                <span>{email}</span>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    setEmails((prev) =>
                                      prev.filter(
                                        (_, itemIdx) => itemIdx !== idx
                                      )
                                    )
                                  }
                                  disabled={loading}
                                >
                                  Remove
                                </Button>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Card.Body>
                      </Card>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <Form.Label className="fw-semibold">
                        {multiple ? "Add another email" : "Email address"}
                      </Form.Label>
                      <InputGroup className="mb-3">
                        <Form.Control
                          name="email"
                          type="email"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          required={!multiple}
                          disabled={loading}
                          placeholder="person@company.com"
                          ref={emailRef}
                        />
                        <Button
                          variant="outline-primary"
                          onClick={handleAddField}
                          disabled={loading || !emailInput.trim()}
                          type="button"
                        >
                          <FontAwesomeIcon
                            icon={faSquarePlus}
                            className="me-2"
                          />
                          Add to list
                        </Button>
                      </InputGroup>

                      <ButtonGroup className="w-100 mb-3 flex-wrap">
                        <Button
                          variant="primary"
                          disabled={loading}
                          className="flex-fill"
                          type="submit"
                        >
                          <FontAwesomeIcon
                            icon={faPaperPlane}
                            className="me-2"
                          />
                          {multiple ? "Send to everyone" : "Send invitation"}
                        </Button>
                        <Button
                          onClick={handleCancel}
                          variant="outline-secondary"
                          className="flex-fill"
                          type="reset"
                          disabled={loading}
                        >
                          <FontAwesomeIcon icon={faUndo} className="me-2" />
                          Reset form
                        </Button>
                      </ButtonGroup>

                      {progressBar && (
                        <ProgressBar
                          striped
                          variant="info"
                          now={progress}
                          label={`${progress}%`}
                          className="mb-0"
                        />
                      )}
                    </Form>
                  </Stack>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4 d-flex flex-column gap-3">
                  <div>
                    <h3 className="h5 mb-2">How invitations work</h3>
                    <p className="text-muted mb-0">
                      We generate a secure sign-in link that lets the new user
                      choose their password. Links expire after a short window
                      for safety.
                    </p>
                  </div>
                  <Alert variant="light" className="border text-muted">
                    <strong>Tip:</strong> Build a list first, then press{" "}
                    <em>Send to everyone</em> to dispatch all invites in one
                    batch.
                  </Alert>
                  <ListGroup variant="flush" className="flex-grow-1">
                    <ListGroup.Item className="px-0">
                      ✅ You can invite both new and returning staff.
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0">
                      ✅ Resetting automatically sends the official portal
                      welcome email.
                    </ListGroup.Item>
                    <ListGroup.Item className="px-0">
                      ✅ Bulk mode keeps progress visible with a live status
                      bar.
                    </ListGroup.Item>
                  </ListGroup>
                  <Button
                    variant="outline-primary"
                    className="mt-auto"
                    onClick={() => navigate("/getLogs")}
                  >
                    Review activity logs
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}
